import React, { useEffect } from "react"
import { TextInput, NumberInput, DateInput, BooleanInput, SelectInput, useGetList, ReferenceInput, AutocompleteInput } from "react-admin"
import { WithFieldAudit, FieldWithAudit } from "./with.field.audit"
import { useFormContext } from "react-hook-form"

interface AuditFieldProps {
  tableName: string
  fieldName?: string
  fieldLabel?: string
  position?: 'end' | 'start'
  layout?: 'inline' | 'addon'
  arrayIndex?: number // Добавляем поддержку индекса массива
  showAuditIcon?: boolean // Добавляем свойство showAuditIcon с возможностью отключения
  [key: string]: any
}

// TextInput с аудитом
export const TextInputWithAudit: React.FC<AuditFieldProps> = ({
  tableName,
  fieldName,
  fieldLabel,
  position = 'end',
  layout = 'addon',
  showAuditIcon = true,
  source,
  label,
  ...props
}) => {
  const effectiveFieldName = fieldName || source
  const effectiveLabel = fieldLabel || label

  if (!showAuditIcon) {
    return <TextInput source={source} label={label} {...props} />
  }

  if (layout === 'inline') {
    return (
      <FieldWithAudit
        tableName={tableName}
        fieldName={effectiveFieldName}
        fieldLabel={effectiveLabel}
      >
        <TextInput source={source} label={label} {...props} />
      </FieldWithAudit>
    )
  }

  return (
    <WithFieldAudit
      tableName={tableName}
      fieldName={effectiveFieldName}
      fieldLabel={effectiveLabel}
      position={position}
    >
      <TextInput source={source} label={label} {...props} />
    </WithFieldAudit>
  )
}

// NumberInput с аудитом
export const NumberInputWithAudit: React.FC<AuditFieldProps> = ({
  tableName,
  fieldName,
  fieldLabel,
  position = 'end',
  layout = 'addon',
  showAuditIcon = true,
  source,
  label,
  ...props
}) => {
  const effectiveFieldName = fieldName || source
  const effectiveLabel = fieldLabel || label

  if (!showAuditIcon) {
    return <NumberInput source={source} label={label} {...props} />
  }

  if (layout === 'inline') {
    return (
      <FieldWithAudit
        tableName={tableName}
        fieldName={effectiveFieldName}
        fieldLabel={effectiveLabel}
      >
        <NumberInput source={source} label={label} {...props} />
      </FieldWithAudit>
    )
  }

  return (
    <WithFieldAudit
      tableName={tableName}
      fieldName={effectiveFieldName}
      fieldLabel={effectiveLabel}
      position={position}
    >
      <NumberInput source={source} label={label} {...props} />
    </WithFieldAudit>
  )
}

// DateInput с аудитом
export const DateInputWithAudit: React.FC<AuditFieldProps> = ({
  tableName,
  fieldName,
  fieldLabel,
  position = 'end',
  layout = 'inline',
  showAuditIcon = true,
  source,
  label,
  ...props
}) => {
  const effectiveFieldName = fieldName || source
  const effectiveLabel = fieldLabel || label

  if (!showAuditIcon) {
    return <DateInput source={source} label={label} {...props} />
  }

  if (layout === 'inline') {
    return (
      <FieldWithAudit
        tableName={tableName}
        fieldName={effectiveFieldName}
        fieldLabel={effectiveLabel}
      >
        <DateInput source={source} label={label} {...props} />
      </FieldWithAudit>
    )
  }

  return (
    <WithFieldAudit
      tableName={tableName}
      fieldName={effectiveFieldName}
      fieldLabel={effectiveLabel}
      position={position}
    >
      <DateInput source={source} label={label} {...props} />
    </WithFieldAudit>
  )
}

// BooleanInput с аудитом
export const BooleanInputWithAudit: React.FC<AuditFieldProps> = ({
  tableName,
  fieldName,
  fieldLabel,
  layout = 'inline',
  showAuditIcon = true,
  source,
  label,
  ...props
}) => {
  const effectiveFieldName = fieldName || source
  const effectiveLabel = fieldLabel || label

  if (!showAuditIcon) {
    return <BooleanInput source={source} label={label} {...props} />
  }

  // BooleanInput обычно лучше с inline layout
  return (
    <FieldWithAudit
      tableName={tableName}
      fieldName={effectiveFieldName}
      fieldLabel={effectiveLabel}
    >
      <BooleanInput source={source} label={label} {...props} />
    </FieldWithAudit>
  )
}

// SelectInput с аудитом — используем WithFieldAudit, внутри которого иконка добавляется оверлеем
export const SelectInputWithAudit: React.FC<AuditFieldProps> = ({
  tableName,
  fieldName,
  fieldLabel,
  position = 'end',
  layout = 'addon',
  showAuditIcon = true,
  source,
  label,
  ...props
}) => {
  const effectiveFieldName = fieldName || source
  const effectiveLabel = fieldLabel || label

  if (!showAuditIcon) {
    return <SelectInput source={source} label={label} {...props} />
  }

  return (
    <WithFieldAudit
      tableName={tableName}
      fieldName={effectiveFieldName}
      fieldLabel={effectiveLabel}
      position={position}
    >
      <SelectInput 
        source={source} 
        label={label} 
        {...props} 
      />
    </WithFieldAudit>
  )
}

// ReferenceInput с аудитом
export const ReferenceInputWithAudit: React.FC<AuditFieldProps & { reference: string }> = ({
  tableName,
  fieldName,
  fieldLabel,
  position = 'end',
  layout = 'inline', // По умолчанию inline для ReferenceInput
  showAuditIcon = true,
  source,
  label,
  reference,
  children,
  ...props
}) => {
  const effectiveFieldName = fieldName || source
  const effectiveLabel = fieldLabel || label

  if (!showAuditIcon) {
    return (
      <ReferenceInput source={source} reference={reference} label={label} {...props}>
        {children || <AutocompleteInput optionText="name" />}
      </ReferenceInput>
    )
  }

  if (layout === 'inline') {
    return (
      <FieldWithAudit
        tableName={tableName}
        fieldName={effectiveFieldName}
        fieldLabel={effectiveLabel}
      >
        <ReferenceInput source={source} reference={reference} label={label} {...props}>
          {children || <AutocompleteInput optionText="name" />}
        </ReferenceInput>
      </FieldWithAudit>
    )
  }

  return (
    <WithFieldAudit
      tableName={tableName}
      fieldName={effectiveFieldName}
      fieldLabel={effectiveLabel}
      position={position}
    >
      <ReferenceInput source={source} reference={reference} label={label} {...props}>
        {children || <AutocompleteInput optionText="name" />}
      </ReferenceInput>
    </WithFieldAudit>
  )
}

// AutocompleteInput с аудитом
export const AutocompleteInputWithAudit: React.FC<AuditFieldProps> = ({
  tableName,
  fieldName,
  fieldLabel,
  position = 'end',
  layout = 'addon',
  showAuditIcon = true,
  source,
  label,
  ...props
}) => {
  const effectiveFieldName = fieldName || source
  const effectiveLabel = fieldLabel || label

  if (!showAuditIcon) {
    return <AutocompleteInput source={source} label={label} {...props} />
  }

  if (layout === 'inline') {
    return (
      <FieldWithAudit
        tableName={tableName}
        fieldName={effectiveFieldName}
        fieldLabel={effectiveLabel}
      >
        <AutocompleteInput source={source} label={label} {...props} />
      </FieldWithAudit>
    )
  }

  return (
    <WithFieldAudit
      tableName={tableName}
      fieldName={effectiveFieldName}
      fieldLabel={effectiveLabel}
      position={position}
    >
      <AutocompleteInput source={source} label={label} {...props} />
    </WithFieldAudit>
  )
}

export const AutoSelectSearchReferenceInputWithAudit: React.FC<AuditFieldProps> = ({
  tableName,
  fieldName,
  fieldLabel,
  layout = 'addon',
  showAuditIcon = true,
  source,
  label,
  reference,
  filter,
  ...props
}) => {

      const { data, isLoading } = useGetList(reference, { filter })
      const { setValue } = useFormContext()
  
      useEffect(() => {
          if (!data || isLoading) return
          // Если только один вариант и значение ещё не выбрано — автозаполнить
          if (data.length === 1) {
              setValue(source, data[0].id)
          }
          // Не сбрасываем значение если вариантов больше одного!
      }, [isLoading, data, setValue, source])
  
      return (
          <ReferenceInput
              source={source}
              filter={filter}
              reference={reference}
              filterToQuery={(searchText: string) => ({ name: searchText })}
          >
              <AutocompleteInputWithAudit
                  tableName={tableName}
                  source={source}
                  label={label}
                  showAuditIcon={showAuditIcon}
                  {...props}
              />
          </ReferenceInput>
      )
}


const AuditInputComponents = {
  TextInputWithAudit,
  NumberInputWithAudit,
  DateInputWithAudit,
  BooleanInputWithAudit,
  SelectInputWithAudit,
  ReferenceInputWithAudit,
  AutocompleteInputWithAudit,
  AutoSelectSearchReferenceInputWithAudit,
}

export default AuditInputComponents
